import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadSimple, FilePdf, FileDoc, CheckCircle, X } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadCV = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: 'pending',
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: true,
  });

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const fileObj of files) {
      try {
        setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 'uploading' }));

        const formData = new FormData();
        formData.append('file', fileObj.file);

        await axios.post(`${API}/upload-cv`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 'success' }));
        successCount++;
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadProgress((prev) => ({ ...prev, [fileObj.id]: 'error' }));
        errorCount++;
      }
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} CV(s) uploaded and parsed successfully!`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} CV(s) failed to upload`);
    }

    // Clear successfully uploaded files after 2 seconds
    setTimeout(() => {
      setFiles((prev) => prev.filter((f) => uploadProgress[f.id] !== 'success'));
      setUploadProgress({});
    }, 2000);
  };

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="upload-cv-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Upload CVs
          </h1>
          <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
            Upload PDF or DOCX files to automatically extract candidate information
          </p>
        </div>

        <div
          {...getRootProps()}
          className={`upload-zone rounded-lg p-12 text-center cursor-pointer ${
            isDragActive ? 'drag-active' : ''
          }`}
          data-testid="upload-dropzone"
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center">
            <UploadSimple size={64} weight="duotone" className="text-slate-400 mb-4" />
            <p className="text-lg font-medium text-slate-800 mb-2">
              {isDragActive ? 'Drop files here' : 'Drag & drop CV files here'}
            </p>
            <p className="text-sm text-slate-600 mb-4">or click to browse files</p>
            <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">
              Supports PDF and DOCX
            </p>
          </div>
        </div>

        {files.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Selected Files ({files.length})
              </h2>
              <button
                onClick={uploadFiles}
                disabled={uploading}
                className="btn-primary px-6 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="upload-button"
              >
                {uploading ? 'Uploading...' : 'Upload All'}
              </button>
            </div>

            <div className="space-y-3">
              {files.map((fileObj) => {
                const isPdf = fileObj.file.name.endsWith('.pdf');
                const status = uploadProgress[fileObj.id];

                return (
                  <div
                    key={fileObj.id}
                    className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg"
                    data-testid={`file-item-${fileObj.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      {isPdf ? (
                        <FilePdf size={32} weight="duotone" className="text-red-500" />
                      ) : (
                        <FileDoc size={32} weight="duotone" className="text-blue-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-900">{fileObj.file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(fileObj.file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {status === 'uploading' && (
                        <span className="text-sm text-slate-600">Uploading...</span>
                      )}
                      {status === 'success' && (
                        <CheckCircle size={24} weight="fill" className="text-green-500" />
                      )}
                      {status === 'error' && (
                        <span className="text-sm text-red-500">Failed</span>
                      )}
                      {!status && (
                        <button
                          onClick={() => removeFile(fileObj.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-file-${fileObj.id}`}
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadCV;